<template>
  <div class="container-fluid p-4">

    <!-- Encabezado -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 class="mb-0"><i class="bi bi-person-lines-fill me-2"></i>Estado de Cuenta de Clientes</h4>
        <p class="text-muted small mb-0">Facturas a crédito pendientes de cobro</p>
      </div>
      <button class="btn btn-outline-secondary btn-sm" @click="cargar" :disabled="cargando">
        <i class="bi bi-arrow-clockwise"></i> Actualizar
      </button>
    </div>

    <!-- Filtros -->
    <div class="card mb-4 shadow-sm">
      <div class="card-body py-2">
        <div class="row gy-2 align-items-end">
          <div class="col-md-6">
            <label class="form-label small mb-1">Buscar cliente</label>
            <div class="input-group input-group-sm">
              <input
                type="text"
                class="form-control form-control-sm"
                placeholder="RUC, cédula o nombre"
                v-model="filtros.busqueda"
                @keyup.enter="cargar"
              />
              <button class="btn btn-outline-primary btn-sm" @click="cargar"><i class="bi bi-search"></i></button>
            </div>
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Estado crédito</label>
            <select class="form-select form-select-sm" v-model="filtros.estadoCredito" @change="cargar">
              <option value="">Todos</option>
              <option value="PENDIENTE">Pendiente</option>
              <option value="PARCIAL">Parcialmente pagado</option>
              <option value="VENCIDO">Vencido</option>
              <option value="PAGADO">Pagado</option>
            </select>
          </div>
          <div class="col-md-2 d-flex align-items-end">
            <button class="btn btn-outline-secondary btn-sm w-100" @click="limpiarFiltros">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Resumen totales -->
    <div v-if="!cargando && facturas.length > 0" class="row gy-2 mb-4">
      <div class="col-md-4">
        <div class="card border-primary text-center py-2">
          <div class="fw-bold fs-5 text-primary">{{ facturas.length }}</div>
          <div class="small text-muted">Facturas a crédito</div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card border-warning text-center py-2">
          <div class="fw-bold fs-5 text-warning">$ {{ totalPendiente.toFixed(2) }}</div>
          <div class="small text-muted">Saldo pendiente</div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card border-danger text-center py-2">
          <div class="fw-bold fs-5 text-danger">{{ facturasVencidas }}</div>
          <div class="small text-muted">Facturas vencidas</div>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="card shadow-sm">
      <div class="card-body p-0">
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-2 text-muted">Cargando estado de cuenta...</p>
        </div>
        <div v-else-if="facturas.length === 0" class="text-center py-5 text-muted">
          <i class="bi bi-inbox fs-2 d-block mb-2"></i>
          No se encontraron facturas a crédito con los filtros aplicados
        </div>
        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>N.º Factura</th>
                <th>Emisión</th>
                <th>Cliente</th>
                <th class="text-end">Total factura</th>
                <th class="text-end">Cobrado</th>
                <th class="text-end">Saldo pendiente</th>
                <th>Vencimiento</th>
                <th>Estado</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in facturasPaginadas" :key="f.pago_id" :class="{'table-danger': f.vencida}">
                <td class="small">{{ f.secuencial }}</td>
                <td class="small">{{ formatFecha(f.fecha_emision) }}</td>
                <td class="small">{{ f.cliente_nombre }}</td>
                <td class="text-end small">$ {{ parseFloat(f.total || 0).toFixed(2) }}</td>
                <td class="text-end small text-success">$ {{ parseFloat(f.monto_pagado || 0).toFixed(2) }}</td>
                <td class="text-end small fw-bold" :class="f.vencida ? 'text-danger' : 'text-warning'">
                  $ {{ parseFloat(f.saldo_pendiente || 0).toFixed(2) }}
                </td>
                <td class="small" :class="{'text-danger fw-semibold': f.vencida}">
                  {{ formatFecha(f.fecha_vencimiento_credito) || calcularVencimiento(f) }}
                </td>
                <td>
                  <span :class="badgeEstado(f.estado_credito)">
                    {{ f.estado_credito || 'PENDIENTE' }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="btn-group btn-group-sm">
                    <button
                      class="btn btn-outline-secondary btn-sm"
                      title="Ver RIDE"
                      :disabled="cargandoRide[f.factura_id]"
                      @click="verRide(f.factura_id)"
                    >
                      <span v-if="cargandoRide[f.factura_id]" class="spinner-border spinner-border-sm"></span>
                      <i v-else class="bi bi-file-earmark-pdf"></i>
                    </button>
                    <button
                      v-if="f.estado_credito !== 'PAGADO' && puedeCobrar"
                      class="btn btn-success btn-sm"
                      title="Registrar cobro"
                      @click="abrirCobro(f)"
                    >
                      <i class="bi bi-cash-coin"></i> Cobrar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Paginación -->
        <div v-if="totalPaginas > 1" class="d-flex justify-content-between align-items-center px-3 py-2 border-top small">
          <span class="text-muted">Mostrando {{ paginaDesde }}–{{ paginaHasta }} de {{ facturas.length }}</span>
          <nav aria-label="Paginación">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: pagina === 1 }">
                <button class="page-link" @click="pagina--" :disabled="pagina === 1">‹</button>
              </li>
              <li v-for="p in paginasVisibles" :key="p" class="page-item" :class="{ active: pagina === p, disabled: p === '...' }">
                <button class="page-link" @click="p !== '...' && (pagina = p)">{{ p }}</button>
              </li>
              <li class="page-item" :class="{ disabled: pagina === totalPaginas }">
                <button class="page-link" @click="pagina++" :disabled="pagina === totalPaginas">›</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Alerta -->
    <div v-if="alerta.mensaje" :class="`alert alert-${alerta.tipo} mt-3 alert-dismissible`" role="alert">
      {{ alerta.mensaje }}
      <button type="button" class="btn-close" @click="alerta.mensaje = ''"></button>
    </div>

    <!-- ── Modal de cobro ──────────────────────────────────────────── -->
    <div v-if="cobro.visible" class="modal-fondo" @click.self="cerrarCobro">
      <div class="modal-caja">
        <div class="modal-cabecera">
          <h5 class="mb-0"><i class="bi bi-cash-coin me-2 text-success"></i>Registrar Cobro</h5>
          <button class="btn-cerrar" @click="cerrarCobro">&times;</button>
        </div>

        <div class="modal-cuerpo">
          <!-- Detalle de la factura -->
          <div class="detalle-factura mb-3">
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Cliente</span>
              <span class="detalle-valor">{{ cobro.pago.cliente_nombre }}</span>
            </div>
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Factura</span>
              <span class="detalle-valor"># {{ cobro.pago.secuencial }}</span>
            </div>
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Total crédito</span>
              <span class="detalle-valor">$ {{ parseFloat(cobro.pago.total_credito || cobro.pago.total || 0).toFixed(2) }}</span>
            </div>
            <div class="detalle-fila">
              <span class="detalle-etiqueta">Ya cobrado</span>
              <span class="detalle-valor text-success">$ {{ parseFloat(cobro.pago.monto_pagado || 0).toFixed(2) }}</span>
            </div>
            <div class="detalle-fila detalle-saldo">
              <span class="detalle-etiqueta fw-bold">Saldo pendiente</span>
              <span class="detalle-valor fw-bold text-danger">$ {{ parseFloat(cobro.pago.saldo_pendiente || 0).toFixed(2) }}</span>
            </div>
          </div>

          <!-- Formulario de cobro -->
          <div class="mb-3">
            <label class="form-label small fw-semibold">Monto a cobrar <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input
                type="number"
                class="form-control"
                step="0.01"
                min="0.01"
                :max="parseFloat(cobro.pago.saldo_pendiente || 0)"
                v-model="cobro.monto_abono"
                @focus="$event.target.select()"
              />
            </div>
            <div class="form-text">Máximo: $ {{ parseFloat(cobro.pago.saldo_pendiente || 0).toFixed(2) }}</div>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Observación <span class="text-muted">(opcional)</span></label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Ej: pago en efectivo, transferencia #123..."
              v-model="cobro.observacion"
              maxlength="200"
            />
          </div>

          <!-- Fuente de cobro -->
          <div class="mb-3">
            <label class="form-label small fw-semibold">Cobrar en <span class="text-danger">*</span></label>
            <div class="d-flex gap-2 mb-2">
              <button
                type="button"
                :class="['btn btn-sm flex-fill', cobro.fuente_cobro === 'caja' ? 'btn-success' : 'btn-outline-secondary']"
                @click="cobro.fuente_cobro = 'caja'; cobro.caja_id = cajas[0]?.id ?? null"
              >
                <i class="bi bi-cash-coin me-1"></i> Caja
              </button>
              <button
                type="button"
                :class="['btn btn-sm flex-fill', cobro.fuente_cobro === 'banco' ? 'btn-success' : 'btn-outline-secondary']"
                @click="cobro.fuente_cobro = 'banco'; cobro.banco_id = bancos[0]?.id ?? null"
              >
                <i class="bi bi-bank me-1"></i> Banco
              </button>
            </div>
            <template v-if="cobro.fuente_cobro === 'caja'">
              <select v-model="cobro.caja_id" class="form-select form-select-sm">
                <option :value="null">-- Seleccionar caja --</option>
                <option v-for="c in cajas" :key="c.id" :value="c.id">{{ c.nombre_caja }}</option>
              </select>
              <div v-if="cajas.length === 0" class="form-text text-warning">No hay cajas disponibles</div>
            </template>
            <template v-if="cobro.fuente_cobro === 'banco'">
              <select v-model="cobro.banco_id" class="form-select form-select-sm">
                <option :value="null">-- Seleccionar banco --</option>
                <option v-for="b in bancos" :key="b.id" :value="b.id">{{ b.nombre_banco }} — {{ b.numero_cuenta }}</option>
              </select>
              <div v-if="bancos.length === 0" class="form-text text-warning">No hay cuentas bancarias configuradas</div>
            </template>
          </div>

          <!-- Error -->
          <div v-if="cobro.error" class="alert alert-danger py-2 small">{{ cobro.error }}</div>
        </div>

        <div class="modal-pie">
          <button class="btn btn-outline-secondary btn-sm" @click="cerrarCobro" :disabled="cobro.cargando">
            Cancelar
          </button>
          <button
            class="btn btn-success btn-sm"
            @click="confirmarCobro"
            :disabled="cobro.cargando || !cobro.monto_abono || parseFloat(cobro.monto_abono) <= 0"
          >
            <span v-if="cobro.cargando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check-circle me-1"></i>
            Confirmar cobro
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/services/api'

const cobroVacio = () => ({
  visible:      false,
  pago:         null,
  monto_abono:  '',
  observacion:  '',
  fuente_cobro: 'caja',  // 'caja' | 'banco'
  caja_id:      null,
  banco_id:     null,
  cargando:     false,
  error:        '',
})

export default {
  name: 'ClientEstadoCuenta',

  data() {
    return {
      cargandoRide: {},
      cargando: false,
      facturas: [],
      bancos:   [],
      cajas:    [],
      pagina: 1,
      porPagina: 25,
      filtros: {
        busqueda: '',
        estadoCredito: '',
      },
      alerta: { mensaje: '', tipo: 'danger' },
      cobro: cobroVacio(),
    }
  },

  computed: {
    puedeCobrar() {
      return this.$store.getters.puedoHacer('/ventas/facturacion', 'crear')
    },
    totalPendiente() {
      return this.facturas.reduce((s, f) => s + parseFloat(f.saldo_pendiente || 0), 0)
    },
    facturasVencidas() {
      return this.facturas.filter(f => f.vencida).length
    },
    totalPaginas() {
      return Math.ceil(this.facturas.length / this.porPagina) || 1
    },
    paginaDesde() {
      return (this.pagina - 1) * this.porPagina + 1
    },
    paginaHasta() {
      return Math.min(this.pagina * this.porPagina, this.facturas.length)
    },
    facturasPaginadas() {
      const desde = (this.pagina - 1) * this.porPagina
      return this.facturas.slice(desde, desde + this.porPagina)
    },
    paginasVisibles() {
      const total = this.totalPaginas
      const actual = this.pagina
      const paginas = []
      if (total <= 7) {
        for (let i = 1; i <= total; i++) paginas.push(i)
      } else {
        paginas.push(1)
        if (actual > 3) paginas.push('...')
        for (let i = Math.max(2, actual - 1); i <= Math.min(total - 1, actual + 1); i++) paginas.push(i)
        if (actual < total - 2) paginas.push('...')
        paginas.push(total)
      }
      return paginas
    },
  },

  mounted() {
    this.cargar()
    this.cargarFuentesCobro()
  },

  methods: {
    async cargarFuentesCobro() {
      try {
        const [resBancos, resCajas] = await Promise.all([
          api.get('/bancos'),
          api.get('/cajas'),
        ])
        this.bancos = resBancos.data?.bancos || resBancos.data || []
        this.cajas  = resCajas.data?.cajas  || resCajas.data  || []
      } catch {
        // No bloquear la vista
      }
    },
    async cargar() {
      this.cargando = true
      this.pagina = 1
      this.alerta = { mensaje: '', tipo: 'danger' }
      try {
        const params = { limit: 500 }
        if (this.filtros.busqueda) params.q = this.filtros.busqueda
        if (this.filtros.estadoCredito) params.estado_credito = this.filtros.estadoCredito

        const { data } = await api.get('/facturas/estado-cuenta', { params })
        this.facturas = data.data || []
      } catch (e) {
        this.alerta = { mensaje: 'No se pudo cargar el estado de cuenta.', tipo: 'danger' }
      } finally {
        this.cargando = false
      }
    },

    limpiarFiltros() {
      this.filtros = { busqueda: '', estadoCredito: '' }
      this.cargar()
    },

    // ── Ver RIDE ──────────────────────────────────────────────────────
    async verRide(facturaId) {
      this.cargandoRide = { ...this.cargandoRide, [facturaId]: true }
      try {
        const response = await api.get(`/facturas/${facturaId}/ride`, { responseType: 'blob' })
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url  = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      } catch (e) {
        let mensaje = 'No se pudo cargar el RIDE de la factura.'
        if (e.response?.data instanceof Blob) {
          try {
            const texto = await e.response.data.text()
            const json  = JSON.parse(texto)
            if (json.error) mensaje = json.error
          } catch (_) { /* mantener mensaje genérico */ }
        }
        this.alerta = { mensaje, tipo: 'danger' }
      } finally {
        this.cargandoRide = { ...this.cargandoRide, [facturaId]: false }
      }
    },

    // ── Modal de cobro ──────────────────────────────────────────────
    abrirCobro(f) {
      this.cobro = {
        visible:      true,
        pago:         f,
        monto_abono:  parseFloat(f.saldo_pendiente || 0).toFixed(2),
        observacion:  '',
        fuente_cobro: 'caja',
        caja_id:      this.cajas[0]?.id  ?? null,
        banco_id:     this.bancos[0]?.id ?? null,
        cargando:     false,
        error:        '',
      }
    },

    cerrarCobro() {
      this.cobro = cobroVacio()
    },

    async confirmarCobro() {
      const { pago, monto_abono, observacion, fuente_cobro, caja_id, banco_id } = this.cobro
      if (!pago?.pago_id) return

      const monto = parseFloat(monto_abono)
      if (!monto || monto <= 0) {
        this.cobro.error = 'El monto debe ser mayor a cero'
        return
      }
      const saldoMax = parseFloat(pago.saldo_pendiente || 0)
      if (monto > saldoMax + 0.005) {
        this.cobro.error = `El monto no puede superar el saldo pendiente ($ ${saldoMax.toFixed(2)})`
        return
      }
      if (fuente_cobro === 'caja' && !caja_id) {
        this.cobro.error = 'Selecciona una caja para registrar el cobro'
        return
      }
      if (fuente_cobro === 'banco' && !banco_id) {
        this.cobro.error = 'Selecciona un banco para registrar el cobro'
        return
      }

      this.cobro.cargando = true
      this.cobro.error = ''
      try {
        const payload = {
          monto_abono:  monto,
          observacion:  observacion.trim(),
          fuente_cobro,
        }
        if (fuente_cobro === 'caja')  payload.caja_id  = caja_id
        if (fuente_cobro === 'banco') payload.banco_id = banco_id

        await api.post(`/facturas/pagos/${pago.pago_id}/cobrar`, payload)
        this.cerrarCobro()
        this.alerta = {
          mensaje: `Cobro de $ ${monto.toFixed(2)} registrado correctamente.`,
          tipo: 'success',
        }
        await this.cargar()
      } catch (e) {
        const respuesta = e?.response?.data
        if (respuesta?.codigo === 'CAJA_NO_ABIERTA') {
          this.cobro.error = 'La caja seleccionada no tiene una sesión abierta. Ábrala primero.'
        } else {
          this.cobro.error = respuesta?.error || 'No se pudo registrar el cobro'
        }
      } finally {
        this.cobro.cargando = false
      }
    },

    // ── Utilidades ──────────────────────────────────────────────────
    calcularVencimiento(f) {
      if (f.fecha_vencimiento_credito) return f.fecha_vencimiento_credito.slice(0, 10)
      if (!f.fecha_emision || !f.dias_credito) return '-'
      const emision = new Date(f.fecha_emision)
      emision.setDate(emision.getDate() + parseInt(f.dias_credito || 0))
      return emision.toISOString().slice(0, 10)
    },

    formatFecha(fecha) {
      if (!fecha) return '-'
      return fecha.slice(0, 10)
    },

    badgeEstado(estado) {
      const mapa = {
        PENDIENTE: 'badge bg-warning text-dark',
        PARCIAL:   'badge bg-info text-dark',
        PAGADO:    'badge bg-success',
        VENCIDO:   'badge bg-danger',
      }
      return mapa[estado] || 'badge bg-secondary'
    },
  },
}
</script>

<style scoped>
/* ── Modal ─────────────────────────────────────────────────────────── */
.modal-fondo {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.modal-caja {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.dark-theme .modal-caja {
  background: #1e2535;
  color: #e2e8f0;
}

.modal-cabecera {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc, #edf2f7);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dark-theme .modal-cabecera {
  background: linear-gradient(135deg, #2d3748, #1a202c);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.btn-cerrar {
  background: none;
  border: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #718096;
  cursor: pointer;
  padding: 0 4px;
}
.btn-cerrar:hover { color: #2d3748; }
.dark-theme .btn-cerrar:hover { color: #e2e8f0; }

.modal-cuerpo {
  padding: 20px;
}

.modal-pie {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background: rgba(0, 0, 0, 0.02);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dark-theme .modal-pie {
  background: rgba(255, 255, 255, 0.03);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* ── Detalle factura en modal ──────────────────────────────────────── */
.detalle-factura {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  padding: 12px 14px;
  border-left: 4px solid #48bb78;
}
.dark-theme .detalle-factura {
  background: rgba(255, 255, 255, 0.04);
}
.detalle-fila {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}
.dark-theme .detalle-fila {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.detalle-fila:last-child { border-bottom: none; }
.detalle-saldo {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.detalle-etiqueta { color: #718096; }
.dark-theme .detalle-etiqueta { color: #a0aec0; }
.detalle-valor { font-weight: 500; }
</style>

